import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  ExportBetweenOfficeSetting,
  ContractorExportIntegrationSettingResponse,
  CostDocumentPairSetting,
  ContractorExportSetting,
  ApiIntegration,
  KsefIntegration,
  ContractorPairCostDocumentSetting,
} from "@/models/api/administration/contractors/ContractorExportIntegrationSettingResponse";
import {
  getContractorExportAndIntegrationsSetting,
  editExportBetweenOfficeSetting,
  getCostDocumentCreatorOffice,
  addPairCostDocument,
  removePairCostDocument,
  setContractorExportSetting,
  savePairCostDocumentSettings,
  editApiIntegrationSetting,
  testIntegrationConnection,
} from "@/requests/administration/contractors/contractorExportAndIntegrationAdministration";
import { SelectItem } from "@/models/api/SelectItem";
import { COST_DOCUMENT_PAIR_TYPE } from "@/static/pairCostDocumentTypes";
import _ from "lodash";

export interface AdministrationContractorExportAndIntegrationInterface {
  lockCustomerAreaIdentificationInput: boolean;
  lockContractorSelect: boolean;
  lockDateInput: boolean;
  addPairOfficeDialog: boolean;
  costDocumentPairType: COST_DOCUMENT_PAIR_TYPE;
  pairOfficeSelectItems: Array<SelectItem>;
  availableIntegrations: Array<string>;
  integrationSelectItems: Array<SelectItem>;
  exportBetweenOfficeSetting: ExportBetweenOfficeSetting;
  costDocumentPairSetting: CostDocumentPairSetting;
  contractorExportSetting: ContractorExportSetting;
  pairCostDocumentSetting: ContractorPairCostDocumentSetting;
  apiIntegration: ApiIntegration;
  ksefIntegration: KsefIntegration;
}

export const AdministrationContractorExportAndIntegrationState: AdministrationContractorExportAndIntegrationInterface =
  {
    lockCustomerAreaIdentificationInput: false,
    lockContractorSelect: false,
    lockDateInput: false,
    addPairOfficeDialog: false,
    costDocumentPairType: COST_DOCUMENT_PAIR_TYPE.PairToOriginal,
    pairOfficeSelectItems: new Array<SelectItem>(),
    availableIntegrations: new Array<string>(),
    integrationSelectItems: new Array<SelectItem>(),
    exportBetweenOfficeSetting: new ExportBetweenOfficeSetting(),
    costDocumentPairSetting: new CostDocumentPairSetting(),
    contractorExportSetting: new ContractorExportSetting(),
    apiIntegration: new ApiIntegration(),
    ksefIntegration: new KsefIntegration(),
    pairCostDocumentSetting: new ContractorPairCostDocumentSetting(),
  };

const AdministrationContractorExportAndIntegrationModule = defineModule({
  state: AdministrationContractorExportAndIntegrationState as AdministrationContractorExportAndIntegrationInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setContractorExportAndIntegration(state, param: ContractorExportIntegrationSettingResponse) {
      state.exportBetweenOfficeSetting = param.exportBetweenOfficeSetting;
      state.costDocumentPairSetting = param.costDocumentPairSetting;
      state.contractorExportSetting = param.contractorExportSetting;
      state.integrationSelectItems = param.integrations;
      state.apiIntegration = param.apiIntegration;
      state.availableIntegrations = param.integrationList;
      state.lockCustomerAreaIdentificationInput = !_.isEmpty(param.apiIntegration.customerAreaIdentification);
      state.lockContractorSelect = !_.isEmpty(param.apiIntegration.parentContractorName);
      state.lockDateInput = !_.isEmpty(param.apiIntegration.syncApiFrom);
      state.pairCostDocumentSetting = param.pairCostDocumentSetting;
    },
    setEnableOrderExport(state, param: boolean) {
      state.exportBetweenOfficeSetting.enableOrderExport = param;
    },
    setEnableOrderExportPkp(state, param: boolean) {
      state.exportBetweenOfficeSetting.enableOrderExportPKP = param;
    },
    setPairOfficeSelectItems(state, param: Array<SelectItem>) {
      state.pairOfficeSelectItems = param;
    },
    setAddPairOfficeDialog(state, param: boolean) {
      state.addPairOfficeDialog = param;
    },
    setCostDocumentPairType(state, param: COST_DOCUMENT_PAIR_TYPE) {
      state.costDocumentPairType = param;
    },
    setContractorExportSetting(state, param: ContractorExportSetting) {
      state.contractorExportSetting = param;
    },
    setCustomerAreaIdentification(state, param: null | string) {
      state.apiIntegration.customerAreaIdentification = param;
    },
    setPairCostDocumentSettings(state, param: ContractorPairCostDocumentSetting) {
      state.pairCostDocumentSetting = param;
    },
  },
  actions: {
    async downloadContractorExportAndIntegration(context) {
      const { commit, rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const response = await getContractorExportAndIntegrationsSetting(
          rootState.administration.contractors.list.selectedContractorId as string
        );
        commit.setContractorExportAndIntegration(response);
      }
    },
    async saveExportBetweenOfficeSetting(context) {
      const { state, dispatch, rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await editExportBetweenOfficeSetting(
          rootState.administration.contractors.list.selectedContractorId,
          state.exportBetweenOfficeSetting
        );
        await dispatch.downloadContractorExportAndIntegration();
      }
    },
    async getCostDocumentCreatorOffice(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getCostDocumentCreatorOffice();
      commit.setPairOfficeSelectItems(response);
    },
    async addPairCostDocument(context, selectedOfficeId: string) {
      const { state, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await addPairCostDocument(rootState.administration.contractors.list.selectedContractorId, {
          OfficeCreatorId: selectedOfficeId,
          PairType: state.costDocumentPairType,
        });
        await dispatch.downloadContractorExportAndIntegration();
      }
    },
    async removePairCostDocument(context, pairCostDocumentId: number) {
      const { rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await removePairCostDocument(
          rootState.administration.contractors.list.selectedContractorId,
          pairCostDocumentId
        );
        await dispatch.downloadContractorExportAndIntegration();
      }
    },
    async setContractorExportSetting(context) {
      const { state, rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await setContractorExportSetting(
          rootState.administration.contractors.list.selectedContractorId,
          state.contractorExportSetting
        );
        await dispatch.downloadContractorExportAndIntegration();
      }
    },
    async savePairCostDocumentSettings(context, param: ContractorPairCostDocumentSetting) {
      const { rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await savePairCostDocumentSettings(rootState.administration.contractors.list.selectedContractorId, param);
      }
    },
    async saveApiIntegrations(context, param: null | string) {
      const { state, rootState, dispatch, commit } = _moduleActionContext(context);
      if (state.apiIntegration.customerAreaIdentification === "") commit.setCustomerAreaIdentification(null);

      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await editApiIntegrationSetting(rootState.administration.contractors.list.selectedContractorId, {
          CustomerAreaIdentification: state.apiIntegration.customerAreaIdentification,
          IntegrationType: state.apiIntegration.integrationType,
          ParentContractorId: param,
          SyncApiFrom: state.apiIntegration.syncApiFrom,
        });
        await dispatch.downloadContractorExportAndIntegration();
      }
    },
    async connectionTest(context) {
      const { state } = _moduleActionContext(context);
      await testIntegrationConnection(state.contractorExportSetting.integrationId as string);
    },
  },
});

export default AdministrationContractorExportAndIntegrationModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorExportAndIntegrationModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(
    context,
    AdministrationContractorExportAndIntegrationModule
  );
